// Passkey second factor (apps/pipeline_api/src/mfa.rs; docs/pipeline/V1-CONTRACT.md "Second factor").
// The server sends WebAuthn options as JSON with base64url binary fields; the browser API wants
// ArrayBuffers, and its results go back as JSON the same way. Nothing here is stored in the browser.
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "./api";

export type EnrollBasis = "allowance" | "bootstrap" | "verified_session";

/** `GET /api/auth/mfa`: the one "who am I" an MFA-pending session may read. */
export interface MfaState {
  required: boolean;
  verified: boolean;
  pending: boolean;
  user: { id: string; email: string; display_name: string; status: string };
  has_passkey: boolean;
  /** Why registering a passkey is allowed now; null = not allowed. */
  enroll_basis: EnrollBasis | null;
  enrollment_expires_at: string | null;
  /** Duo is set up on the server and this sign-in has a verified identity to give it (src/duo.rs). */
  duo_available: boolean;
  /** The Duo username (the verified Microsoft sign-in address), when `duo_available`. */
  duo_username: string | null;
  /** Whether the user can request a Duo setup email on the MFA screen (`/api/auth/mfa/duo/enroll`). */
  duo_enroll_available?: boolean;
}

export interface PasskeySummary {
  id: string;
  label: string;
  created_at: string;
  last_used_at: string | null;
}

export const mfaKeys = {
  state: ["mfa", "state"] as const,
  passkeys: ["mfa", "passkeys"] as const,
};

export function useMfaState() {
  return useQuery({ queryKey: mfaKeys.state, queryFn: () => api<MfaState>("/api/auth/mfa"), retry: false });
}

export function usePasskeys() {
  return useQuery({ queryKey: mfaKeys.passkeys, queryFn: () => api<PasskeySummary[]>("/api/auth/mfa/passkeys") });
}

// ---- base64url ----

export function fromB64url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function toB64url(buf: ArrayBuffer | ArrayBufferView): string {
  const bytes = buf instanceof ArrayBuffer ? new Uint8Array(buf) : new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---- options from the server ----

type Json = Record<string, unknown>;
interface CredentialRef {
  type: "public-key";
  id: string;
  transports?: AuthenticatorTransport[];
}

function refs(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return (list as CredentialRef[]).map((c) => ({ type: "public-key", id: fromB64url(c.id), ...(c.transports ? { transports: c.transports } : {}) }));
}

/** `register/start` → `navigator.credentials.create({ publicKey })` options. */
export function creationOptions(json: { publicKey: Json }): PublicKeyCredentialCreationOptions {
  const pk = json.publicKey;
  const user = pk.user as { id: string; name: string; displayName: string };
  return {
    ...(pk as unknown as PublicKeyCredentialCreationOptions),
    challenge: fromB64url(pk.challenge as string),
    user: { ...user, id: fromB64url(user.id) },
    excludeCredentials: refs(pk.excludeCredentials),
  };
}

/** `verify/start` → `navigator.credentials.get({ publicKey })` options. */
export function requestOptions(json: { publicKey: Json }): PublicKeyCredentialRequestOptions {
  const pk = json.publicKey;
  return {
    ...(pk as unknown as PublicKeyCredentialRequestOptions),
    challenge: fromB64url(pk.challenge as string),
    allowCredentials: refs(pk.allowCredentials),
  };
}

// ---- results to the server ----

/** A `create()` result as the server reads it (webauthn-rs `RegisterPublicKeyCredential`). */
export function registrationJson(c: PublicKeyCredential): Json {
  const r = c.response as AuthenticatorAttestationResponse;
  return {
    id: c.id,
    rawId: toB64url(c.rawId),
    type: c.type,
    extensions: c.getClientExtensionResults?.() ?? {},
    response: {
      attestationObject: toB64url(r.attestationObject),
      clientDataJSON: toB64url(r.clientDataJSON),
      transports: typeof r.getTransports === "function" ? r.getTransports() : undefined,
    },
  };
}

/** A `get()` result as the server reads it (webauthn-rs `PublicKeyCredential`). */
export function assertionJson(c: PublicKeyCredential): Json {
  const r = c.response as AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: toB64url(c.rawId),
    type: c.type,
    extensions: c.getClientExtensionResults?.() ?? {},
    response: {
      authenticatorData: toB64url(r.authenticatorData),
      clientDataJSON: toB64url(r.clientDataJSON),
      signature: toB64url(r.signature),
      userHandle: r.userHandle ? toB64url(r.userHandle) : null,
    },
  };
}

// ---- ceremonies ----

export function passkeysSupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window && !!navigator.credentials;
}

/** Registers a passkey on this device: start, the browser prompt, finish. */
export async function registerPasskey(label?: string): Promise<PasskeySummary> {
  const options = await api<{ publicKey: Json }>("/api/auth/mfa/register/start", { method: "POST", body: {} });
  const cred = (await navigator.credentials.create({ publicKey: creationOptions(options) })) as PublicKeyCredential | null;
  if (!cred) throw new DOMException("No passkey was created", "NotAllowedError");
  return api<PasskeySummary>("/api/auth/mfa/register/finish", {
    method: "POST",
    body: { label: label?.trim() || undefined, credential: registrationJson(cred) },
  });
}

/** Confirms this session with one of the user's passkeys. */
export async function confirmPasskey(): Promise<MfaState> {
  const options = await api<{ publicKey: Json }>("/api/auth/mfa/verify/start", { method: "POST", body: {} });
  const cred = (await navigator.credentials.get({ publicKey: requestOptions(options) })) as PublicKeyCredential | null;
  if (!cred) throw new DOMException("No passkey was used", "NotAllowedError");
  return api<MfaState>("/api/auth/mfa/verify/finish", { method: "POST", body: { credential: assertionJson(cred) } });
}

/** A plain-language message for a failed ceremony (browser errors and the server's codes). */
export function passkeyError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case "enrollment_not_allowed":
        return "Ask an administrator to enable 2FA enrollment for your account.";
      case "challenge_expired":
      case "challenge_missing":
        return "The passkey request timed out. Try again.";
      case "mfa_failed":
        return "That passkey didn't confirm your sign-in. Use the passkey you set up for LoanShip, or ask an administrator to reset your 2FA.";
      case "passkey_rejected":
        return "That passkey couldn't be registered. Make sure your device asks for your fingerprint, face or PIN, then try again.";
      case "conflict":
        return "That passkey is already registered.";
      case "no_passkey":
        return "You don't have a passkey yet.";
      default:
        return e.message;
    }
  }
  if (e instanceof DOMException || (e instanceof Error && e.name)) {
    switch ((e as Error).name) {
      case "NotAllowedError":
        return "The passkey prompt was cancelled or timed out. Try again when you're ready.";
      case "InvalidStateError":
        return "This device already has a passkey for your account.";
      case "NotSupportedError":
        return "This browser or device can't create a passkey. Try another browser, or your phone.";
      case "SecurityError":
        return "Passkeys only work on the LoanShip address itself. Open LoanShip from its normal link and try again.";
      case "AbortError":
        return "The passkey prompt was closed. Try again.";
    }
  }
  return "Something went wrong with the passkey. Try again.";
}
